/**
 * Supervisor timing/task authoring (post-sixth-demo, Leo 2026-06-23).
 *
 * A "Start from" toggle offers two creation approaches over ONE shared model:
 *
 *  - Start from days  — the working-hours picker at the top, then tasks placed
 *    within each day (Day / Table views).
 *  - Start from tasks — the task list first (each task's days + times), with the
 *    working-hours picker below.
 *
 * BOTH modes show the working hours (DailyHoursPicker owns `requested_schedule`)
 * and let you set each task's start/end CLOCK TIME within the day's window. The
 * toggle only changes whether the plan is organized by day or by task; flipping
 * preserves the work. Evaluation-only: only the working-hours schedule is
 * persisted, not the per-day task detail. See timingModel.ts.
 *
 * Render note: per-row helpers are plain functions CALLED inline (not nested
 * <Components>) so React keeps element identity and text inputs don't lose focus.
 */
import { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, Sparkles, Trash2 } from 'lucide-react';
import type {
  CrewRoleLines,
  IntakeProposedTask,
  LaborRates,
  ProjectKind,
  ScheduleDay,
} from '@vari-crew/schemas';
import { blendedLabor, defaultTasksForKind, EMPTY_CREW_ROLE_LINES } from '@vari-crew/schemas';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { DailyHoursPicker } from '@/components/ui/DailyHoursPicker';
import { useLaborRates } from '@/api/labor-rates';
import { formatMoneyCents } from '@/lib/format';
import { cn } from '@/lib/cn';
import {
  type CrewRole,
  type RoleCounts,
  type TimingLayout,
  type TimingMode,
  type TimingModel,
  type TimingDay,
  allocEffHours,
  allocManHours,
  clampTime,
  dayLabel,
  dayManHours,
  dayPlannedHours,
  dayRange,
  EMPTY_ROLES,
  modelToProposedTasks,
  newTaskId,
  reconcileModel,
  ROLE_ABBR,
  ROLE_KEYS,
  ROLE_LABELS,
  seedTasks,
  TASK_TYPE_OPTIONS,
  totalManHours,
  typeMeta,
  windowHours,
} from './timingModel';

const MODE_KEY = 'vari.timingMode';
const VIEW_KEY = 'vari.daysView';
const TIME_CLS = 'h-8 rounded-md border border-line bg-transparent px-1.5 text-sm';

export function TimingAuthoring({
  start,
  end,
  schedule,
  onScheduleChange,
  includeWeekends,
  flexible,
  onFlexibleChange,
  kind,
  clientLabel,
  proposedTasks,
  initialModel,
  onModelChange,
  onLaborChange,
  onProposedTasksChange,
}: {
  start: string | null;
  end: string | null;
  schedule: ScheduleDay[] | null;
  onScheduleChange: (schedule: ScheduleDay[] | null) => void;
  includeWeekends?: boolean;
  flexible?: boolean;
  onFlexibleChange?: (v: boolean) => void;
  /** Project kind + client label for the opt-in "Suggest tasks for this kind"
   *  seeder (D-5). Defaults are seeded on demand, never auto-injected. */
  kind?: ProjectKind;
  clientLabel?: string;
  proposedTasks?: IntakeProposedTask[];
  /** Persisted authoring model to rehydrate from (intake session cache / resumed
   *  draft). Used ONCE at mount; when present it wins over the proposedTasks seed
   *  so the full per-block roles/times/descriptions survive a remount. */
  initialModel?: TimingModel | null;
  /** Emits the full authoring model on every edit so the panel can persist it
   *  (→ session cache + server draft) and rehydrate it via initialModel. */
  onModelChange?: (model: TimingModel) => void;
  /** Emits the labor derived from the task plan (role lines + cents) for the bid. */
  onLaborChange?: (labor: { roles: CrewRoleLines; cents: number }) => void;
  /** Emits the authored plan as persistable tasks (one per allocation-on-a-day),
   *  each with its placement, description, head count, and per-role demand. */
  onProposedTasksChange?: (tasks: IntakeProposedTask[]) => void;
}) {
  const { data: rates } = useLaborRates();
  const roleRates: Record<CrewRole, number> = {
    lead: rates?.lead ?? 7500,
    installer: rates?.installer ?? 7500,
    driver: rates?.driver ?? 7500,
    trainee: rates?.trainee ?? 7500,
  };

  const readMode = (): TimingMode => {
    try {
      const v = localStorage.getItem(MODE_KEY);
      if (v === 'days' || v === 'tasks') return v;
    } catch {
      /* ignore */
    }
    return 'days';
  };
  const [mode, setMode] = useState<TimingMode>(readMode);
  const [view, setView] = useState<TimingLayout>(() => {
    try {
      const v = localStorage.getItem(VIEW_KEY);
      if (v === 'day' || v === 'table') return v;
    } catch {
      /* ignore */
    }
    return 'day';
  });
  const pickMode = (m: TimingMode) => {
    setMode(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* ignore */
    }
  };
  const pickView = (v: TimingLayout) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* ignore */
    }
  };

  const dayKeys = dayRange(start, end, includeWeekends ?? false);
  const dayKeysStr = dayKeys.join('|');
  const scheduleKey = JSON.stringify(schedule ?? []);

  // Rehydrate the persisted model (a remount / resumed draft) so the full plan
  // survives; otherwise seed from the proposed-task hint. Either way the reconcile
  // effect below aligns it to the current range + working-hours windows.
  const [model, setModel] = useState<TimingModel>(
    () =>
      initialModel ??
      reconcileModel({ days: [], tasks: seedTasks(proposedTasks) }, dayKeys, schedule, readMode()),
  );

  // Reconcile to the range + the working-hours windows (owned by the picker in
  // both modes). Re-clamps task times if a window changed; seeds new days per mode.
  useEffect(() => {
    setModel((m) => reconcileModel(m, dayKeys, schedule, mode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayKeysStr, scheduleKey, mode]);

  // ---- mutators -----------------------------------------------------------
  function patchDay(date: string, fn: (d: TimingDay) => TimingDay) {
    setModel((m) => ({ ...m, days: m.days.map((d) => (d.date === date ? fn(d) : d)) }));
  }
  function patchAlloc(
    date: string,
    i: number,
    fn: (a: TimingModel['days'][number]['allocs'][number]) => TimingModel['days'][number]['allocs'][number],
  ) {
    patchDay(date, (d) => ({ ...d, allocs: d.allocs.map((a, idx) => (idx === i ? fn(a) : a)) }));
  }
  // Task start/end — clamped to fall within the day's working-hours window.
  const setTime = (date: string, i: number, field: 'start' | 'end', v: string) =>
    patchDay(date, (d) => ({
      ...d,
      allocs: d.allocs.map((a, idx) =>
        idx === i ? { ...a, [field]: clampTime(v, d.window.start, d.window.end) } : a,
      ),
    }));
  const bumpRole = (date: string, i: number, role: CrewRole, delta: number) =>
    patchAlloc(date, i, (a) => ({
      ...a,
      roles: { ...a.roles, [role]: Math.max(0, Math.min(99, (a.roles[role] || 0) + delta)) },
    }));
  const setRole = (date: string, i: number, role: CrewRole, count: number) =>
    patchAlloc(date, i, (a) => ({
      ...a,
      roles: { ...a.roles, [role]: Math.max(0, Math.min(99, count)) },
    }));
  const toggleLunch = (date: string, i: number) =>
    patchAlloc(date, i, (a) => ({ ...a, lunch: !a.lunch }));
  const removeAlloc = (date: string, i: number) =>
    setModel((m) => {
      const removed = m.days.find((d) => d.date === date)?.allocs[i];
      const days = m.days.map((d) =>
        d.date === date ? { ...d, allocs: d.allocs.filter((_, idx) => idx !== i) } : d,
      );
      // In days mode each block owns its task — drop it once its last block is gone.
      let tasks = m.tasks;
      if (mode === 'days' && removed && m.tasks.length > 1) {
        const stillUsed = days.some((d) => d.allocs.some((a) => a.taskId === removed.taskId));
        if (!stillUsed) tasks = m.tasks.filter((t) => t.id !== removed.taskId);
      }
      return { ...m, days, tasks };
    });
  function addAlloc(date: string) {
    // A new block is its own task — mint one so it can be labeled independently.
    const id = newTaskId();
    setModel((m) => ({
      ...m,
      tasks: [...m.tasks, { id, type: 'install', name: '', description: '' }],
      days: m.days.map((d) =>
        d.date === date
          ? {
              ...d,
              allocs: [
                ...d.allocs,
                { taskId: id, start: d.window.start, end: d.window.end, roles: { ...EMPTY_ROLES }, lunch: false },
              ],
            }
          : d,
      ),
    }));
  }
  // Toggle a task on/off for a day (the day-box selector in tasks mode).
  function toggleTaskDay(taskId: string, date: string) {
    patchDay(date, (d) => {
      const has = d.allocs.some((a) => a.taskId === taskId);
      return has
        ? { ...d, allocs: d.allocs.filter((a) => a.taskId !== taskId) }
        : {
            ...d,
            allocs: [...d.allocs, { taskId, start: d.window.start, end: d.window.end, roles: { ...EMPTY_ROLES }, lunch: false }],
          };
    });
  }
  const setTaskName = (id: string, name: string) =>
    setModel((m) => ({ ...m, tasks: m.tasks.map((t) => (t.id === id ? { ...t, name } : t)) }));
  const setTaskDescription = (id: string, description: string) =>
    setModel((m) => ({ ...m, tasks: m.tasks.map((t) => (t.id === id ? { ...t, description } : t)) }));
  const setTaskType = (id: string, type: IntakeProposedTask['type']) =>
    setModel((m) => ({ ...m, tasks: m.tasks.map((t) => (t.id === id ? { ...t, type } : t)) }));
  const addTask = () =>
    setModel((m) => ({
      ...m,
      tasks: [...m.tasks, { id: newTaskId(), type: 'install', name: '', description: '' }],
    }));
  const removeTask = (id: string) =>
    setModel((m) =>
      m.tasks.length <= 1
        ? m
        : {
            tasks: m.tasks.filter((t) => t.id !== id),
            days: m.days.map((d) => ({ ...d, allocs: d.allocs.filter((a) => a.taskId !== id) })),
          },
    );
  // Drop an unscheduled (no-allocation) skeleton task. No length guard — flexible
  // task creation allows removing every task down to a zero-task plan; a skeleton
  // has no day blocks to strand.
  const removeSkeletonTask = (id: string) =>
    setModel((m) => ({ ...m, tasks: m.tasks.filter((t) => t.id !== id) }));
  // Opt-in seeder (D-5): drop the kind's default task spine in as bare skeleton
  // tasks (name + type, no schedule/crew/hours), which the supervisor then
  // schedules. Never auto-injected — only when the supervisor clicks the button.
  const suggestTasks = () => {
    const defaults = defaultTasksForKind(kind ?? 'install', clientLabel || 'job');
    if (defaults.length === 0) return;
    setModel((m) => ({
      ...m,
      tasks: [
        ...m.tasks,
        ...defaults.map((t) => ({ id: newTaskId(), type: t.type, name: t.title, description: '' })),
      ],
    }));
  };

  const taskById = (id: string) => model.tasks.find((t) => t.id === id);
  const totalMH = totalManHours(model);
  // Collapse the per-day/per-role crew into one CrewRoleLines for the bid: per
  // role, the peak head count and the man-hours ÷ that count as "hours each".
  const derivedRoleLines: CrewRoleLines = useMemo(() => {
    const lines: CrewRoleLines = {
      lead: { count: 0, hours: 0 },
      installer: { count: 0, hours: 0 },
      driver: { count: 0, hours: 0 },
      trainee: { count: 0, hours: 0 },
    };
    for (const role of ROLE_KEYS) {
      let manHours = 0;
      let maxCount = 0;
      for (const d of model.days) {
        for (const a of d.allocs) {
          const c = a.roles[role] || 0;
          if (c > 0) {
            manHours += c * allocEffHours(a);
            if (c > maxCount) maxCount = c;
          }
        }
      }
      if (maxCount > 0) {
        lines[role] = { count: maxCount, hours: Math.round((manHours / maxCount) * 10) / 10 };
      }
    }
    return lines;
  }, [model]);
  const fullRates: LaborRates = { ...roleRates, estimate_default: rates?.estimate_default ?? 7500 };
  const derivedBlended = blendedLabor(derivedRoleLines, fullRates);
  const laborCents = derivedBlended.labor_cents;
  const blendedCents = derivedBlended.blended_rate_cents;

  // Emit the derived labor to the bid whenever it changes (deduped).
  const laborEmitKey = JSON.stringify({ r: derivedRoleLines, c: laborCents });
  useEffect(() => {
    onLaborChange?.({ roles: derivedRoleLines, cents: laborCents });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborEmitKey]);

  // Emit the authored plan as persistable proposed tasks (deduped) so it becomes
  // real tasks on create — flowing to dispatch / calendar / crew screens.
  const proposedFromPlan = useMemo(() => modelToProposedTasks(model), [model]);
  const proposedEmitKey = JSON.stringify(proposedFromPlan);
  useEffect(() => {
    onProposedTasksChange?.(proposedFromPlan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedEmitKey]);

  // Emit the full model on every edit so the panel persists it (session cache +
  // server draft) and can rehydrate it via initialModel — keeping per-block
  // roles/times/descriptions across a remount, not just the flattened tasks.
  useEffect(() => {
    onModelChange?.(model);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);
  const hasDays = model.days.length > 0;

  // Skeleton tasks — minted but never placed on a day. They persist bare (no
  // schedule / crew / hours) via modelToProposedTasks; flexible task creation
  // (Leo demo #7) lets a project carry not-yet-planned work, filled in later.
  const unscheduledTasks = useMemo(() => {
    const placed = new Set<string>();
    for (const d of model.days) for (const a of d.allocs) placed.add(a.taskId);
    return model.tasks.filter((t) => !placed.has(t.id));
  }, [model]);

  // ---- inline helpers -----------------------------------------------------
  const dot = (type: string) => (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
      style={{ background: typeMeta(type).bar }}
      aria-hidden
    />
  );
  // Short day label for the day-box selector, e.g. "Mon 22".
  const chipLabel = (date: string) => `${dayLabel(date).split(',')[0]} ${Number(date.slice(8))}`;
  // Label a block's task directly in the row — type + name (like the task-first
  // cards). Each days-mode block is its own task, so this edits just that block.
  const taskInline = (taskId: string) => {
    const t = taskById(taskId);
    return (
      <span className="inline-flex items-center gap-1.5 flex-1 min-w-[180px]">
        {dot(t?.type ?? 'install')}
        <select
          value={t?.type ?? 'install'}
          onChange={(e) => t && setTaskType(t.id, e.target.value as IntakeProposedTask['type'])}
          className="h-8 rounded-md border border-line bg-transparent px-1.5 text-sm"
          aria-label="Task type"
        >
          {TASK_TYPE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {typeMeta(opt).label}
            </option>
          ))}
        </select>
        <Input
          value={t?.name ?? ''}
          onChange={(e) => t && setTaskName(t.id, e.target.value)}
          placeholder="Task name"
          className="h-8 flex-1 min-w-[80px]"
        />
      </span>
    );
  };
  // Per-task description textarea (both modes).
  const descriptionField = (taskId: string) => {
    const t = taskById(taskId);
    return (
      <Textarea
        value={t?.description ?? ''}
        onChange={(e) => t && setTaskDescription(t.id, e.target.value)}
        rows={2}
        placeholder="Description / details (optional)"
        className="text-sm w-full"
      />
    );
  };
  const timePair = (date: string, i: number, startV: string, endV: string) => (
    <span className="inline-flex items-center gap-1">
      <input
        type="time"
        aria-label="Task start"
        value={startV}
        onChange={(e) => setTime(date, i, 'start', e.target.value)}
        className={TIME_CLS}
      />
      <span className="text-ink-faint text-xs">–</span>
      <input
        type="time"
        aria-label="Task end"
        value={endV}
        onChange={(e) => setTime(date, i, 'end', e.target.value)}
        className={TIME_CLS}
      />
    </span>
  );
  const lunchChip = (date: string, i: number, on: boolean) => (
    <button
      type="button"
      onClick={() => toggleLunch(date, i)}
      className={cn(
        'text-[11px] px-3 h-7 rounded-full border whitespace-nowrap min-w-[4.75rem] text-center transition-colors',
        on
          ? 'border-accent text-accent bg-accent/10 font-medium'
          : 'border-line text-ink-faint hover:bg-fill-soft',
      )}
    >
      {on ? 'Lunch −1h' : '+ lunch'}
    </button>
  );
  // Per-role crew badge. Every role is always shown and toggleable: an OFF role is
  // a dashed ghost pill you click to add (→ 1); an ON role is a solid pill with its
  // head count in an embedded bubble + −/+ steppers (− at 1 toggles it back off).
  const roleBadge = (date: string, i: number, r: CrewRole, count: number) => {
    if (count <= 0) {
      return (
        <button
          key={r}
          type="button"
          aria-pressed={false}
          aria-label={`Add ${ROLE_LABELS[r]}`}
          onClick={() => setRole(date, i, r, 1)}
          className="inline-flex items-center h-7 px-3 rounded-full border border-dashed border-line text-[11px] text-ink-faint hover:text-accent hover:border-accent/50 hover:bg-accent/5 transition-colors"
        >
          {ROLE_ABBR[r]}
        </button>
      );
    }
    return (
      <span
        key={r}
        aria-label={`${ROLE_LABELS[r]}: ${count}`}
        className="inline-flex items-center h-7 rounded-full border border-accent/40 bg-accent/5 text-[11px] overflow-hidden"
      >
        <button
          type="button"
          aria-label={count <= 1 ? `Remove ${ROLE_LABELS[r]}` : `Fewer ${ROLE_LABELS[r]}`}
          onClick={() => bumpRole(date, i, r, -1)}
          className="pl-2 pr-1 h-7 grid place-items-center text-ink-faint hover:text-accent hover:bg-accent/10"
        >
          <Minus className="w-3 h-3" />
        </button>
        <span className="inline-flex items-center gap-1 px-0.5">
          <span className="font-medium text-ink-dim">{ROLE_ABBR[r]}</span>
          <span className="grid place-items-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-accent text-accent-fg text-[10px] font-semibold tabular-nums">
            {count}
          </span>
        </span>
        <button
          type="button"
          aria-label={`More ${ROLE_LABELS[r]}`}
          onClick={() => bumpRole(date, i, r, 1)}
          className="pl-1 pr-2 h-7 grid place-items-center text-ink-faint hover:text-accent hover:bg-accent/10"
        >
          <Plus className="w-3 h-3" />
        </button>
      </span>
    );
  };
  // Every role as a toggleable badge (no "add role" menu — all four are always shown).
  const roleEditor = (date: string, i: number, roles: RoleCounts) => (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {ROLE_KEYS.map((r) => roleBadge(date, i, r, roles[r] || 0))}
    </span>
  );
  const removeBtn = (date: string, i: number) => (
    <button
      type="button"
      aria-label="Remove"
      onClick={() => removeAlloc(date, i)}
      className="text-ink-faint hover:text-status-blocked-fg"
    >
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );

  const workingHours = () => (
    <DailyHoursPicker
      start={start}
      end={end}
      value={schedule}
      onChange={onScheduleChange}
      flexible={flexible}
      onFlexibleChange={onFlexibleChange}
      includeWeekends={includeWeekends}
    />
  );

  const rollup = () => (
    <div className="rounded-lg border border-line bg-fill-soft/40 p-3 space-y-1.5">
      <div className="flex flex-wrap gap-x-6 gap-y-1 items-baseline">
        <span className="text-sm">
          <span className="text-ink-faint">Total man-hours</span>{' '}
          <span className="font-semibold tabular-nums">{totalMH}</span>
        </span>
        <span className="text-sm">
          <span className="text-ink-faint">Labor preview</span>{' '}
          <span className="font-semibold tabular-nums">{formatMoneyCents(laborCents)}</span>{' '}
          <span className="text-[11px] text-ink-faint">@ blended {formatMoneyCents(blendedCents)}/hr</span>
        </span>
      </div>
      <p className="text-[11px] text-ink-faint">
        Man-hours = hours × crew; labor prices each role at its configured rate. Mileage, debris &amp;
        the final quoted total live in the bid section below.
      </p>
    </div>
  );

  // "Unscheduled / not yet planned" lane — bare tasks (name + optional type) that
  // haven't been placed on a day or given crew/hours. They persist as-is and can
  // be scheduled, crewed, and estimated later. Distinct from the day grid above.
  const unscheduledLane = () => (
    <div className="rounded-md border border-dashed border-line p-3 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <Label>Unscheduled · not yet planned</Label>
        <span className="text-[11px] text-ink-faint">No date, crew, or hours yet — add them later.</span>
      </div>
      {unscheduledTasks.length > 0 ? (
        <div className="space-y-2">
          {unscheduledTasks.map((t) => (
            <div key={t.id} className="flex items-center gap-2">
              {dot(t.type)}
              <select
                value={t.type}
                onChange={(e) => setTaskType(t.id, e.target.value as IntakeProposedTask['type'])}
                className="h-8 rounded-md border border-line bg-transparent px-1.5 text-sm"
                aria-label="Task type"
              >
                {TASK_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {typeMeta(opt).label}
                  </option>
                ))}
              </select>
              <Input
                value={t.name}
                onChange={(e) => setTaskName(t.id, e.target.value)}
                placeholder="Task name"
                className="h-8 flex-1"
              />
              <button
                type="button"
                aria-label="Remove task"
                onClick={() => removeSkeletonTask(t.id)}
                className="text-ink-faint hover:text-status-blocked-fg"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-ink-faint">No unscheduled tasks.</p>
      )}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={addTask}
          className="text-xs text-accent hover:underline inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> Add unscheduled task
        </button>
        {kind && kind !== 'other' && (
          <button
            type="button"
            onClick={suggestTasks}
            className="text-xs text-ink-dim hover:text-accent hover:underline inline-flex items-center gap-1"
          >
            <Sparkles className="w-3.5 h-3.5" /> Suggest tasks for this kind
          </button>
        )}
      </div>
    </div>
  );

  // The legacy days-mode "Tasks — names & details" list was removed — tasks are
  // now labeled inline in each block row (see taskInline); the name/type lives on
  // the task itself, edited wherever the block appears.

  // ---- DAYS views ---------------------------------------------------------
  const dayView = () => (
    <div className="space-y-3">
      {model.days.map((d) => {
        const planned = dayPlannedHours(d);
        const win = windowHours(d);
        const over = planned > win + 0.001;
        return (
          <div key={d.date} className="rounded-md border border-line p-3 space-y-2.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-medium">{dayLabel(d.date)}</span>
              <span className="text-[11px] text-ink-faint">
                Allowed window {d.window.start}–{d.window.end}
              </span>
              <span className="text-xs text-ink-dim">
                Day total · <span className="font-medium text-ink tabular-nums">{dayManHours(d)} mh</span>
              </span>
            </div>
            <div className="space-y-2">
              {d.allocs.map((a, i) => (
                <div
                  key={i}
                  className="space-y-1.5 border-t border-line/60 pt-2.5 first:border-t-0 first:pt-0"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {taskInline(a.taskId)}
                    <span className="text-sm font-medium tabular-nums w-16 text-right ml-auto">
                      {allocManHours(a)} mh
                    </span>
                    {removeBtn(d.date, i)}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pl-5">
                    {timePair(d.date, i, a.start, a.end)}
                    {lunchChip(d.date, i, a.lunch)}
                    {roleEditor(d.date, i, a.roles)}
                  </div>
                  <div className="pl-5">{descriptionField(a.taskId)}</div>
                </div>
              ))}
              {d.allocs.length === 0 && (
                <p className="text-xs text-ink-faint">No tasks planned this day yet.</p>
              )}
            </div>
            {over && (
              <p className="text-[11px] text-status-blocked-fg">
                Planned task time exceeds the {win}-hour working window.
              </p>
            )}
            <button
              type="button"
              onClick={() => addAlloc(d.date)}
              className="text-xs text-accent hover:underline inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add task block
            </button>
          </div>
        );
      })}
    </div>
  );

  const tableView = () => (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] text-ink-faint text-left">
            <th className="font-medium px-2.5 py-1.5">Day · allowed window</th>
            <th className="font-medium px-2.5 py-1.5">Task</th>
            <th className="font-medium px-2.5 py-1.5">Time</th>
            <th className="font-medium px-2.5 py-1.5">Roles</th>
            <th className="font-medium px-2.5 py-1.5 text-right">Man-h</th>
            <th className="px-2.5 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {model.days.map((d) =>
            (d.allocs.length ? d.allocs : [null]).map((a, i) => (
              <tr key={`${d.date}-${i}`} className="border-t border-line">
                {i === 0 && (
                  <td
                    rowSpan={Math.max(1, d.allocs.length)}
                    className="px-2.5 py-2 bg-fill-soft/50 align-top whitespace-nowrap"
                  >
                    <div className="font-medium">{dayLabel(d.date)}</div>
                    <div className="text-[11px] text-ink-faint mt-0.5">
                      {d.window.start}–{d.window.end}
                    </div>
                  </td>
                )}
                {a ? (
                  <>
                    <td className="px-2.5 py-1.5 min-w-[220px]">{taskInline(a.taskId)}</td>
                    <td className="px-2.5 py-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        {timePair(d.date, i, a.start, a.end)}
                        {lunchChip(d.date, i, a.lunch)}
                      </span>
                    </td>
                    <td className="px-2.5 py-1.5 text-center">{roleEditor(d.date, i, a.roles)}</td>
                    <td className="px-2.5 py-1.5 text-right font-medium tabular-nums">{allocManHours(a)}</td>
                    <td className="px-2.5 py-1.5 text-right">{removeBtn(d.date, i)}</td>
                  </>
                ) : (
                  <td colSpan={5} className="px-2.5 py-1.5">
                    <button
                      type="button"
                      onClick={() => addAlloc(d.date)}
                      className="text-xs text-accent hover:underline inline-flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add task block
                    </button>
                  </td>
                )}
              </tr>
            )),
          )}
        </tbody>
        <tfoot>
          <tr className="border-t border-line">
            <td colSpan={4} className="px-2.5 py-2 text-right text-ink-faint">
              Total man-hours
            </td>
            <td className="px-2.5 py-2 text-right font-semibold tabular-nums">{totalMH}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );

  const daysMode = () => (
    <div className="space-y-3">
      {!hasDays ? (
        <p className="text-xs text-ink-faint border border-dashed border-line rounded-md p-3">
          Pick a start day above to schedule tasks — or add unscheduled tasks below to plan later.
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <Label>Tasks within each day</Label>
            <div className="flex rounded-md border border-line overflow-hidden text-[11px]">
              {(['day', 'table'] as TimingLayout[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => pickView(v)}
                  className={cn(
                    'px-2.5 py-1 transition-colors capitalize',
                    view === v ? 'bg-accent/10 text-accent font-medium' : 'text-ink-dim hover:bg-fill-soft',
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          {view === 'day' ? dayView() : tableView()}
          {rollup()}
        </>
      )}
      {/* Bare tasks with no day/crew/hours — distinct from the day grid above. */}
      {unscheduledLane()}
    </div>
  );

  // ---- TASKS mode ---------------------------------------------------------
  const tasksMode = () => (
    <div className="space-y-3">
      {!hasDays ? (
        <p className="text-xs text-ink-faint border border-dashed border-line rounded-md p-3">
          Pick the project days on the calendar above, then list the tasks and assign them.
        </p>
      ) : (
        <>
          {model.tasks.map((t) => {
            const rows: { date: string; i: number; alloc: TimingModel['days'][number]['allocs'][number]; win: { start: string; end: string } }[] = [];
            for (const d of model.days) {
              d.allocs.forEach((a, i) => {
                if (a.taskId === t.id) rows.push({ date: d.date, i, alloc: a, win: d.window });
              });
            }
            const sub = Math.round(rows.reduce((s, r) => s + allocManHours(r.alloc), 0) * 10) / 10;
            return (
              <div key={t.id} className="rounded-md border border-line p-3 space-y-2.5">
                <div className="flex items-center gap-2">
                  {dot(t.type)}
                  <select
                    value={t.type}
                    onChange={(e) => setTaskType(t.id, e.target.value as IntakeProposedTask['type'])}
                    className="h-8 rounded-md border border-line bg-transparent px-1.5 text-sm"
                    aria-label="Task type"
                  >
                    {TASK_TYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {typeMeta(opt).label}
                      </option>
                    ))}
                  </select>
                  <Input
                    value={t.name}
                    onChange={(e) => setTaskName(t.id, e.target.value)}
                    placeholder="Name / details"
                    className="h-8 flex-1"
                  />
                  <span className="text-sm text-ink-dim tabular-nums whitespace-nowrap">{sub} mh</span>
                  <button
                    type="button"
                    aria-label="Remove task"
                    onClick={() => removeTask(t.id)}
                    disabled={model.tasks.length <= 1}
                    className="text-ink-faint hover:text-status-blocked-fg disabled:opacity-30"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {descriptionField(t.id)}
                <div className="space-y-2 pl-4">
                  {rows.map((r) => (
                    <div key={`${r.date}-${r.i}`} className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-ink-dim w-24 shrink-0 whitespace-nowrap">
                        {dayLabel(r.date)}
                      </span>
                      {timePair(r.date, r.i, r.alloc.start, r.alloc.end)}
                      <span className="text-[11px] text-ink-faint w-28 shrink-0">
                        within {r.win.start}–{r.win.end}
                      </span>
                      {lunchChip(r.date, r.i, r.alloc.lunch)}
                      {roleEditor(r.date, r.i, r.alloc.roles)}
                      <span className="text-sm font-medium tabular-nums w-16 text-right">{allocManHours(r.alloc)} mh</span>
                      {removeBtn(r.date, r.i)}
                    </div>
                  ))}
                  {rows.length === 0 && (
                    <p className="text-xs text-ink-faint">Not assigned to a day yet.</p>
                  )}
                </div>
                {/* Day-box selector along the bottom — click any calendar day to
                    toggle this task on/off for it (incl. days other tasks use). */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-ink-faint mr-0.5">Runs on</span>
                  {model.days.map((d) => {
                    const on = d.allocs.some((a) => a.taskId === t.id);
                    return (
                      <button
                        key={d.date}
                        type="button"
                        onClick={() => toggleTaskDay(t.id, d.date)}
                        aria-pressed={on}
                        className={cn(
                          'px-2 py-1 rounded-md text-[11px] border transition-colors',
                          on
                            ? 'bg-accent/10 text-accent border-accent font-medium'
                            : 'border-line text-ink-dim hover:bg-fill-soft',
                        )}
                      >
                        {chipLabel(d.date)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addTask}
            className="text-xs text-accent hover:underline inline-flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Add task
          </button>
          {rollup()}
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Shared working-hours selector — above "Plan the work", present in both
          modes. Task start/end times in either mode are constrained to these. */}
      {workingHours()}
      <div className="space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Label>Plan the work</Label>
          <div className="flex items-center gap-3">
            <div className="flex rounded-md border border-line overflow-hidden text-[11px]">
              {([
                { id: 'days', label: 'Start from days' },
                { id: 'tasks', label: 'Start from tasks' },
              ] as { id: TimingMode; label: string }[]).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => pickMode(m.id)}
                  className={cn(
                    'px-2.5 py-1 transition-colors',
                    mode === m.id ? 'bg-accent/10 text-accent font-medium' : 'text-ink-dim hover:bg-fill-soft',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-ink-dim tabular-nums">
              {totalMH} mh · {formatMoneyCents(laborCents)}
            </span>
          </div>
        </div>
        <p className="text-[11px] text-ink-faint">
          {mode === 'days'
            ? 'Place tasks (with start/end times) within each day — bounded by the working hours above.'
            : 'List the tasks and give each its days + start/end times — bounded by the working hours above.'}
        </p>
        {mode === 'days' ? daysMode() : tasksMode()}
      </div>
    </div>
  );
}
